import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff, Volume2, VolumeX, Play, Pause, Sparkles, Music, MessageCircle, Phone, PhoneOff, Heart, Loader2 } from 'lucide-react';
import api from '../utils/api';

// Digital Narad character images
const NARAD_IMAGES = {
  cosmic: "https://customer-assets.emergentagent.com/job_022c69bd-26f5-4a3b-92eb-c3d146e50ed4/artifacts/2zgjy9hc_Vishnu_purana_characters_202602242333.jpeg",
  temple: "https://customer-assets.emergentagent.com/job_022c69bd-26f5-4a3b-92eb-c3d146e50ed4/artifacts/ei7yv80y_Vishnu_purana_characters_202602242331.jpeg",
  traditional: "https://customer-assets.emergentagent.com/job_022c69bd-26f5-4a3b-92eb-c3d146e50ed4/artifacts/tmds949r_Vishnu_purana_characters_202602242329%20%281%29.jpeg"
};

// Spiritual content
const SPIRITUAL_CONTENT = [
  { type: 'mantra', text: "Om Namo Narayanaya", hindi: "ॐ नमो नारायणाय" },
  { type: 'mantra', text: "Hare Krishna Hare Rama", hindi: "हरे कृष्ण हरे राम" },
  { type: 'bhajan', text: "Narayan Narayan Jai Jai Govind Hare", hindi: "नारायण नारायण" },
  { type: 'mantra', text: "Om Jai Jagdish Hare", hindi: "ॐ जय जगदीश हरे" },
];

const DigitalNaradTV = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visitorName, setVisitorName] = useState('');
  const [hasGreeted, setHasGreeted] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showNameInput, setShowNameInput] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSpiritualMode, setShowSpiritualMode] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [isHiddenByAdmin, setIsHiddenByAdmin] = useState(false); // NEW: Admin visibility control
  
  // Emotion state for avatar expressions
  const [currentEmotion, setCurrentEmotion] = useState('happy');
  
  // Animation states for realistic movements
  const [mouthOpenness, setMouthOpenness] = useState(0);
  const [headRotation, setHeadRotation] = useState({ x: 0, y: 0, z: 0 });
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  const [blinkState, setBlinkState] = useState(false);
  const [breathePhase, setBreathePhase] = useState(0);
  const [gesture, setGesture] = useState('idle');
  const [shoulderMovement, setShouldernMovement] = useState(0);
  const [handGesture, setHandGesture] = useState('rest');
  
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const lipSyncFrameRef = useRef(null);
  const audioSourceRef = useRef(null);
  
  // Advanced lip-sync states
  const [lipSyncData, setLipSyncData] = useState({
    mouthOpen: 0,      // 0-1 vertical mouth opening
    mouthWidth: 0.5,   // 0-1 horizontal mouth stretch
    jawDrop: 0,        // 0-1 jaw movement
    tongueVisible: false,
    lipTension: 0,     // 0-1 lip tightness
    volume: 0,         // Current audio volume
    pitch: 0,          // Estimated pitch (low/mid/high)
  });
  
  // Emotion configurations for avatar expressions
  const EMOTION_CONFIG = {
    happy: { 
      auraColor: 'rgba(234,179,8,0.4)', 
      glowColor: '#fbbf24',
      emoji: '😊',
      headTilt: 5,
      eyeBrightness: 1.2
    },
    excited: { 
      auraColor: 'rgba(34,197,94,0.5)', 
      glowColor: '#22c55e',
      emoji: '🎉',
      headTilt: 8,
      eyeBrightness: 1.4
    },
    caring: { 
      auraColor: 'rgba(236,72,153,0.4)', 
      glowColor: '#ec4899',
      emoji: '💝',
      headTilt: -3,
      eyeBrightness: 1.0
    },
    sympathetic: { 
      auraColor: 'rgba(147,51,234,0.4)', 
      glowColor: '#9333ea',
      emoji: '🤗',
      headTilt: -5,
      eyeBrightness: 0.9
    },
    encouraging: { 
      auraColor: 'rgba(59,130,246,0.5)', 
      glowColor: '#3b82f6',
      emoji: '💪',
      headTilt: 3,
      eyeBrightness: 1.3
    },
    playful: { 
      auraColor: 'rgba(251,146,60,0.5)', 
      glowColor: '#fb923c',
      emoji: '😄',
      headTilt: 10,
      eyeBrightness: 1.3
    },
    grateful: { 
      auraColor: 'rgba(234,179,8,0.5)', 
      glowColor: '#eab308',
      emoji: '🙏',
      headTilt: -8,
      eyeBrightness: 1.1
    },
    curious: { 
      auraColor: 'rgba(6,182,212,0.4)', 
      glowColor: '#06b6d4',
      emoji: '🤔',
      headTilt: 12,
      eyeBrightness: 1.2
    }
  };
  
  const emotionConfig = EMOTION_CONFIG[currentEmotion] || EMOTION_CONFIG.happy;

  useEffect(() => {
    fetchSettings();
    initSession();
    initSpeechRecognition();
    startIdleAnimations();
    
    const storedName = localStorage.getItem('narad_visitor_name');
    if (storedName) {
      setVisitorName(storedName);
      setHasGreeted(true);
    }

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (lipSyncFrameRef.current) cancelAnimationFrame(lipSyncFrameRef.current);
      if (audioRef.current) audioRef.current.pause();
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Real-time lip-sync audio analysis
  const startLipSyncAnalysis = useCallback(() => {
    if (!audioRef.current) return;
    
    try {
      // Create audio context if not exists
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // Create analyser if not exists
      if (!analyserRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.3;
      }
      
      // Connect audio element to analyser (only once)
      if (!audioSourceRef.current) {
        audioSourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
        audioSourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
      }
      
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const timeDataArray = new Uint8Array(bufferLength);
      
      // Smoothing variables for natural movement
      let smoothMouth = 0;
      let smoothWidth = 0.5;
      let smoothJaw = 0;
      
      const analyze = () => {
        if (!isSpeaking) {
          setLipSyncData(prev => ({
            ...prev,
            mouthOpen: 0,
            mouthWidth: 0.5,
            jawDrop: 0,
            volume: 0,
          }));
          return;
        }
        
        analyserRef.current.getByteFrequencyData(dataArray);
        analyserRef.current.getByteTimeDomainData(timeDataArray);
        
        // Calculate volume from frequency data
        const sum = dataArray.reduce((a, b) => a + b, 0);
        const avgVolume = sum / bufferLength / 255;
        
        // Analyze frequency bands for more realistic lip-sync
        const lowFreq = dataArray.slice(0, 10).reduce((a, b) => a + b, 0) / 10 / 255;  // Bass/Jaw
        const midFreq = dataArray.slice(10, 40).reduce((a, b) => a + b, 0) / 30 / 255; // Mouth opening
        const highFreq = dataArray.slice(40, 80).reduce((a, b) => a + b, 0) / 40 / 255; // Consonants/lip tension
        
        // Calculate amplitude variation for natural movement
        let maxAmp = 0;
        let minAmp = 255;
        for (let i = 0; i < bufferLength; i++) {
          if (timeDataArray[i] > maxAmp) maxAmp = timeDataArray[i];
          if (timeDataArray[i] < minAmp) minAmp = timeDataArray[i];
        }
        const amplitude = (maxAmp - minAmp) / 255;
        
        // Smooth transitions for natural movement
        const smoothFactor = 0.3;
        smoothMouth = smoothMouth * (1 - smoothFactor) + (midFreq * 0.6 + amplitude * 0.4) * smoothFactor;
        smoothWidth = smoothWidth * (1 - smoothFactor) + (0.4 + highFreq * 0.4) * smoothFactor;
        smoothJaw = smoothJaw * (1 - smoothFactor) + (lowFreq * 0.8) * smoothFactor;
        
        // Determine pitch category
        let pitchCategory = 'mid';
        if (lowFreq > midFreq && lowFreq > highFreq) pitchCategory = 'low';
        else if (highFreq > midFreq) pitchCategory = 'high';
        
        setLipSyncData({
          mouthOpen: Math.min(1, smoothMouth * 1.5),
          mouthWidth: smoothWidth,
          jawDrop: Math.min(1, smoothJaw * 1.2),
          tongueVisible: midFreq > 0.4 && highFreq > 0.3,
          lipTension: highFreq,
          volume: avgVolume,
          pitch: pitchCategory,
        });
        
        lipSyncFrameRef.current = requestAnimationFrame(analyze);
      };
      
      analyze();
    } catch (error) {
      console.error('Lip sync analysis error:', error);
    }
  }, [isSpeaking]);
  
  // Stop lip-sync analysis
  const stopLipSyncAnalysis = useCallback(() => {
    if (lipSyncFrameRef.current) {
      cancelAnimationFrame(lipSyncFrameRef.current);
      lipSyncFrameRef.current = null;
    }
    setLipSyncData({
      mouthOpen: 0,
      mouthWidth: 0.5,
      jawDrop: 0,
      tongueVisible: false,
      lipTension: 0,
      volume: 0,
      pitch: 'mid',
    });
  }, []);

  // Start/stop lip-sync when speaking state changes
  useEffect(() => {
    if (isSpeaking) {
      startLipSyncAnalysis();
    } else {
      stopLipSyncAnalysis();
    }
  }, [isSpeaking, startLipSyncAnalysis, stopLipSyncAnalysis]);

  // Realistic idle animations
  const startIdleAnimations = () => {
    // Breathing animation - natural chest movement
    const breatheInterval = setInterval(() => {
      setBreathePhase(prev => (prev + 0.05) % (Math.PI * 2));
    }, 50);

    // Natural eye blinking (every 2-5 seconds)
    const blinkInterval = setInterval(() => {
      setBlinkState(true);
      setTimeout(() => setBlinkState(false), 120);
    }, 2500 + Math.random() * 2500);

    // Subtle head movement - looking around naturally
    const headInterval = setInterval(() => {
      const time = Date.now() / 1000;
      setHeadRotation({
        x: Math.sin(time * 0.3) * 2,
        y: Math.sin(time * 0.2) * 3,
        z: Math.sin(time * 0.1) * 1
      });
    }, 50);

    // Natural eye movement
    const eyeInterval = setInterval(() => {
      setEyePosition({
        x: (Math.random() - 0.5) * 4,
        y: (Math.random() - 0.5) * 2
      });
    }, 2000 + Math.random() * 1000);

    // Shoulder/body micro-movements
    const shoulderInterval = setInterval(() => {
      setShouldernMovement(Math.sin(Date.now() / 1500) * 2);
    }, 100);

    return () => {
      clearInterval(breatheInterval);
      clearInterval(blinkInterval);
      clearInterval(headInterval);
      clearInterval(eyeInterval);
      clearInterval(shoulderInterval);
    };
  };

  // Auto-greet — ONLY if admin has not disabled Narad
  useEffect(() => {
    if (isHiddenByAdmin) return;                  // 🔒 respect admin off setting
    if (settings === null) return;                // wait until fetchSettings resolves
    if (!hasGreeted && !visitorName) {
      const timer = setTimeout(() => {
        // Double-check at fire-time in case admin toggled mid-flight
        if (!isHiddenByAdmin) {
          speakWithTTS("Arre wah! Swagat hai aapka Anant Web Portal mein! Main hoon Digital Narad, aapka apna dost! Bataaiye na, aapka shubh naam kya hai?");
          setShowNameInput(true);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [hasGreeted, visitorName, isHiddenByAdmin, settings]);

  // Lip sync animation based on audio analysis
  useEffect(() => {
    if (isSpeaking) {
      setGesture('talking');
      setHandGesture('explaining');
      // Mouth openness is now controlled by lipSyncData from audio analysis
      setMouthOpenness(lipSyncData.mouthOpen);
    } else {
      setMouthOpenness(0);
      setHandGesture('rest');
      if (!isListening && !isLoading) {
        setGesture('idle');
      }
    }
  }, [isSpeaking, isListening, isLoading, lipSyncData.mouthOpen]);

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'hi-IN';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results).map(r => r[0].transcript).join('');
        setUserInput(transcript);
        if (event.results[0].isFinal) {
          sendMessage(transcript);
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        setGesture('idle');
      };
    }
  };

  const fetchSettings = async () => {
    try {
      // First check admin visibility settings
      try {
        const visibilityRes = await api.get('/admin/ai-control/public/avatar-visibility');
        const visibility = visibilityRes.data;
        
        // If emergency shutdown or not visible, hide the entire component
        if (visibility.emergency_shutdown || !visibility.narad_avatar_visible || !visibility.narad_enabled) {
          setIsHiddenByAdmin(true);
          return; // Don't fetch other settings
        }
        setIsHiddenByAdmin(false);
      } catch (e) {
        console.log('Could not fetch visibility:', e);
      }
      
      const response = await api.get('/digital-narad/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Settings fetch error:', error);
    }
  };

  const initSession = () => {
    const stored = localStorage.getItem('narad_session_tv');
    if (stored) {
      setSessionId(stored);
    } else {
      const newSession = `narad-tv-${Date.now()}`;
      localStorage.setItem('narad_session_tv', newSession);
      setSessionId(newSession);
    }
  };

  // State for preferred voice
  const [hindiVoice, setHindiVoice] = useState(null);
  const [maleHindiVoice, setMaleHindiVoice] = useState(null);
  const [femaleHindiVoice, setFemaleHindiVoice] = useState(null);
  const [voiceType, setVoiceType] = useState('madhur'); // Default to Madhur (slow Hindi)
  const [availableBrowserVoices, setAvailableBrowserVoices] = useState([]);
  
  // Available voice options - OpenAI + Browser Native Hindi
  const VOICE_OPTIONS = [
    // Browser Native Hindi Voices (FREE)
    { id: 'madhur', name: '🎤 Madhur (Hindi Male)', desc: 'Slow, clear Hindi - FREE', type: 'browser', speed: 0.85, pitch: 0.9, gender: 'male' },
    { id: 'swara', name: '🎤 Swara (Hindi Female)', desc: 'Melodious Hindi - FREE', type: 'browser', speed: 0.85, pitch: 1.3, gender: 'female' },
    { id: 'hindi-natural', name: '🎤 Hindi Natural', desc: 'Browser native - FREE', type: 'browser', speed: 0.9, pitch: 1.0, gender: 'neutral' },
    // Divider
    { id: 'divider', name: '── OpenAI HD Voices ──', desc: '', type: 'divider', disabled: true },
    // OpenAI TTS Voices (uses Emergent key)
    { id: 'shimmer', name: 'Shimmer (Clear Female)', desc: 'HD quality', type: 'openai', speed: 0.85 },
    { id: 'nova', name: 'Nova (Warm Female)', desc: 'Natural tone', type: 'openai', speed: 0.85 },
    { id: 'alloy', name: 'Alloy (Neutral)', desc: 'Balanced', type: 'openai', speed: 0.85 },
    { id: 'echo', name: 'Echo (Male)', desc: 'Warm male', type: 'openai', speed: 0.85 },
    { id: 'onyx', name: 'Onyx (Deep Male)', desc: 'Deep voice', type: 'openai', speed: 0.85 },
    { id: 'fable', name: 'Fable (Storyteller)', desc: 'Expressive', type: 'openai', speed: 0.85 },
  ];
  
  // Initialize browser Hindi voices - find both male and female
  useEffect(() => {
    const loadVoices = () => {
      if ('speechSynthesis' in window) {
        const voices = window.speechSynthesis.getVoices();
        
        // Find all Hindi voices
        const hindiVoices = voices.filter(v => 
          v.lang.includes('hi') || 
          v.name.toLowerCase().includes('hindi') ||
          v.name.includes('हिन्दी')
        );
        
        setAvailableBrowserVoices(hindiVoices);
        
        // Try to find male voice (Hemant, or lower in list)
        const maleVoice = hindiVoices.find(v => 
          v.name.toLowerCase().includes('hemant') ||
          v.name.toLowerCase().includes('male') ||
          v.name.toLowerCase().includes('ravi')
        ) || hindiVoices[0];
        
        // Try to find female voice (Kalpana, Lekha, or any with female indicator)
        const femaleVoice = hindiVoices.find(v => 
          v.name.toLowerCase().includes('kalpana') ||
          v.name.toLowerCase().includes('lekha') ||
          v.name.toLowerCase().includes('female') ||
          v.name.toLowerCase().includes('swara')
        ) || hindiVoices[hindiVoices.length > 1 ? 1 : 0];
        
        setMaleHindiVoice(maleVoice);
        setFemaleHindiVoice(femaleVoice);
        setHindiVoice(maleVoice); // Default to male
        
        if (maleVoice) {
          console.log('Male Hindi voice:', maleVoice.name);
        }
        if (femaleVoice) {
          console.log('Female Hindi voice:', femaleVoice.name);
        }
      }
    };
    
    // Load voices (they may load asynchronously)
    loadVoices();
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  // Browser TTS with proper male/female voice selection
  const speakWithNativeHindi = useCallback((text, emotion = 'happy') => {
    if (!('speechSynthesis' in window)) return;
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'hi-IN';
    
    // Get voice config for the selected voice
    const voiceConfig = VOICE_OPTIONS.find(v => v.id === voiceType) || VOICE_OPTIONS[0];
    
    // Get available voices
    const voices = window.speechSynthesis.getVoices();
    const hindiVoices = voices.filter(v => 
      v.lang.includes('hi') || 
      v.name.toLowerCase().includes('hindi')
    );
    
    // Select the best available Hindi voice
    let selectedBrowserVoice = hindiVoices[0] || hindiVoice;
    
    // Try to find specific voices for gender differentiation
    if (voiceConfig.gender === 'female') {
      // Look for female-sounding voices
      const femaleVoice = hindiVoices.find(v => 
        v.name.toLowerCase().includes('female') ||
        v.name.toLowerCase().includes('kalpana') ||
        v.name.toLowerCase().includes('lekha') ||
        v.name.toLowerCase().includes('swara') ||
        v.name.includes('Google') // Google voices tend to be neutral/female
      );
      if (femaleVoice) selectedBrowserVoice = femaleVoice;
    } else if (voiceConfig.gender === 'male') {
      // Look for male-sounding voices  
      const maleVoice = hindiVoices.find(v => 
        v.name.toLowerCase().includes('male') ||
        v.name.toLowerCase().includes('hemant') ||
        v.name.toLowerCase().includes('ravi') ||
        v.name.toLowerCase().includes('madhur')
      );
      if (maleVoice) selectedBrowserVoice = maleVoice;
    }
    
    // Set the voice if available
    if (selectedBrowserVoice) {
      utterance.voice = selectedBrowserVoice;
    }
    
    // Apply pitch based on voice selection to create distinct male/female sound
    // This is the KEY differentiation - even with same voice, pitch makes them distinct
    if (voiceConfig.id === 'madhur' || voiceConfig.gender === 'male') {
      utterance.pitch = 0.7; // Deep male voice
    } else if (voiceConfig.id === 'swara' || voiceConfig.gender === 'female') {
      utterance.pitch = 1.4; // Higher female voice
    } else {
      utterance.pitch = voiceConfig.pitch || 1.0;
    }
    
    // Use slower speed for clearer Hindi
    const baseSpeed = voiceConfig.speed || 0.85;
    utterance.rate = emotion === 'excited' ? baseSpeed + 0.1 : baseSpeed;
    
    // Log for debugging
    console.log(`Playing voice: ${voiceConfig.name}, pitch: ${utterance.pitch}, rate: ${utterance.rate}`);
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      setGesture('idle');
      setTimeout(() => setCurrentEmotion('happy'), 2000);
    };
    
    window.speechSynthesis.speak(utterance);
  }, [hindiVoice, voiceType]);

  // Smart TTS - uses browser voice for Madhur/Swara, OpenAI for others
  const speakWithSmartTTS = useCallback(async (text, emotion = 'happy') => {
    if (!text) return;
    
    const selectedVoice = VOICE_OPTIONS.find(v => v.id === voiceType);
    
    // Use browser native for Madhur, Swara, hindi-natural
    if (selectedVoice?.type === 'browser') {
      speakWithNativeHindi(text, emotion);
      return;
    }
    
    // Use OpenAI TTS for HD voices
    try {
      const speed = selectedVoice?.speed || 0.85;
      
      const response = await api.post('/digital-narad/tts', {
        text: text,
        voice: voiceType,
        speed: speed,
        emotion: emotion
      });

      if (response.data.audio_base64) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio_base64}`;
        
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.onplay = () => {
            setIsSpeaking(true);
            setGesture('talking');
          };
          audioRef.current.onended = () => {
            setIsSpeaking(false);
            setGesture('idle');
            setTimeout(() => setCurrentEmotion('happy'), 2000);
          };
          audioRef.current.onerror = () => {
            setIsSpeaking(false);
            setGesture('idle');
            // Fallback to browser TTS
            speakWithNativeHindi(text, emotion);
          };
          await audioRef.current.play();
        }
      } else {
        speakWithNativeHindi(text, emotion);
      }
    } catch (error) {
      console.error('TTS error:', error);
      speakWithNativeHindi(text, emotion);
    }
  }, [voiceType, speakWithNativeHindi]);

  // Legacy function - now uses smart TTS
  const speakWithOpenAITTS = useCallback(async (text, emotion = 'happy') => {
    speakWithSmartTTS(text, emotion);
  }, [speakWithSmartTTS]);

  // Speak with TTS (uses OpenAI for quality, falls back to browser)
  const speakWithTTS = useCallback((text, emotion = 'happy') => {
    setDisplayText('');
    setIsTyping(true);
    setGesture('thinking');
    
    // Typewriter effect
    let charIndex = 0;
    const typeInterval = setInterval(() => {
      if (charIndex < text.length) {
        const char = text[charIndex];
        setDisplayText(prev => prev + char);
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, 25);

    // Use OpenAI TTS for better quality
    speakWithOpenAITTS(text, emotion);
  }, [speakWithOpenAITTS]);

  // Fallback to browser TTS
  const fallbackSpeak = useCallback((text) => {
    speakWithNativeHindi(text, currentEmotion);
  }, [speakWithNativeHindi, currentEmotion]);

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
    setGesture('idle');
  };

  const startVoiceInput = async () => {
    if (recognitionRef.current) {
      try {
        setIsListening(true);
        setGesture('listening');
        recognitionRef.current.start();
        
        if (navigator.mediaDevices) {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          audioContextRef.current = new AudioContext();
          analyserRef.current = audioContextRef.current.createAnalyser();
          const source = audioContextRef.current.createMediaStreamSource(stream);
          source.connect(analyserRef.current);
          
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          const updateLevel = () => {
            if (isListening) {
              analyserRef.current?.getByteFrequencyData(dataArray);
              const avg = dataArray.reduce((a, b) => a + b) / dataArray.length;
              setAudioLevel(avg / 255);
              animationFrameRef.current = requestAnimationFrame(updateLevel);
            }
          };
          updateLevel();
        }
      } catch (error) {
        setIsListening(false);
      }
    }
  };

  const stopVoiceInput = () => {
    recognitionRef.current?.stop();
    cancelAnimationFrame(animationFrameRef.current);
    setIsListening(false);
    setAudioLevel(0);
    setGesture('idle');
  };

  const handleNameSubmit = () => {
    if (nameInput.trim()) {
      const name = nameInput.trim();
      setVisitorName(name);
      localStorage.setItem('narad_visitor_name', name);
      setShowNameInput(false);
      setHasGreeted(true);
      setGesture('excited');
      setHandGesture('namaste');
      
      setTimeout(() => {
        speakWithTTS(`Arre wah ${name} ji! Kitna pyaara naam hai aapka! Main bahut khush hoon aapse milke! Dekhiye ${name} ji, main hoon aapka Digital Narad - aapka apna dost! Bataaiye, aaj main aapki kya seva karun?`, 'excited');
      }, 300);
    }
  };

  const sendMessage = async (message) => {
    if (!message.trim()) return;
    
    setGesture('thinking');
    setUserInput('');
    setIsLoading(true);
    
    try {
      // Use chat endpoint for text response (voice will be handled by FREE native TTS)
      const response = await api.post('/digital-narad/chat-with-voice', {
        message: `Visitor name is ${visitorName || 'Guest'}. Their question: ${message}`,
        session_id: sessionId
      });
      
      const { response: aiResponse, emotion } = response.data;
      
      // Set emotion for avatar expression
      const detectedEmotion = emotion || 'happy';
      setCurrentEmotion(detectedEmotion);
      
      // Display text with typewriter
      setDisplayText('');
      setIsTyping(true);
      let charIndex = 0;
      const typeInterval = setInterval(() => {
        if (charIndex < aiResponse.length) {
          const char = aiResponse[charIndex];
          setDisplayText(prev => prev + char);
          charIndex++;
        } else {
          clearInterval(typeInterval);
          setIsTyping(false);
        }
      }, 25);

      setIsLoading(false);
      
      // Use OpenAI TTS for better Hindi voice quality
      speakWithOpenAITTS(aiResponse, detectedEmotion);
      
    } catch (error) {
      setIsLoading(false);
      setCurrentEmotion('caring');
      const fallback = visitorName 
        ? `Arre ${visitorName} ji! Technical issue aa gaya. Par chinta mat karo!`
        : `Arre dost! Technical issue hai. Chinta mat karo!`;
      speakWithTTS(fallback, 'caring');
    }
  };

  const playSpiritualContent = (content) => {
    setGesture('devotional');
    setHandGesture('namaste');
    setCurrentEmotion('grateful');
    const intro = visitorName 
      ? `${visitorName} ji, suniye yeh pavitra ${content.type}: `
      : `Suniye yeh pavitra ${content.type}: `;
    speakWithTTS(intro + content.text, 'grateful');
  };

  const quickActions = [
    { label: "Products", query: "Mujhe aapke best products ke baare mein batao", icon: "🛒" },
    { label: "Sign Up", query: "Main sign up kaise karun?", icon: "📝" },
    { label: "Samadhan", query: "Sankat Mochan Samadhan kya hai?", icon: "🙏" },
    { label: "Rewards", query: "Rewards kaise milte hain?", icon: "🎁" },
    { label: "Courses", query: "Best courses konse hain?", icon: "📚" },
    { label: "Help", query: "Mujhe help chahiye", icon: "❤️" },
  ];

  // Calculate animation transforms
  const breatheScale = 1 + Math.sin(breathePhase) * 0.015;
  const breatheY = Math.sin(breathePhase) * 2;

  // If hidden by admin, don't render anything
  if (isHiddenByAdmin) {
    return null;
  }

  return (
    <section className="py-4 px-4 bg-gradient-to-r from-orange-900/20 via-yellow-900/10 to-red-900/20" data-testid="digital-narad-tv-section">
      {/* Hidden audio element for TTS playback */}
      <audio ref={audioRef} />
      
      <div className="max-w-6xl mx-auto">
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-2xl blur opacity-50" />
          
          <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl border-2 border-yellow-600/50 overflow-hidden shadow-2xl">
            
            {/* Top Bar */}
            <div className="bg-gradient-to-r from-yellow-900/60 via-orange-900/60 to-red-900/60 px-4 py-2 flex items-center justify-between border-b border-yellow-600/30">
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${isSpeaking ? 'bg-green-500' : isListening ? 'bg-red-500' : isLoading ? 'bg-blue-500' : 'bg-yellow-500'} animate-pulse`} />
                <h2 className="text-base font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  Digital Narad - Aapka Apna Dost!
                  <Heart className="w-4 h-4 text-red-400 animate-pulse" />
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs flex items-center gap-1 ${isSpeaking ? 'text-green-400' : isListening ? 'text-red-400' : isLoading ? 'text-blue-400' : 'text-yellow-400'}`}>
                  {isLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                  {isSpeaking ? 'BOL RAHA HOON' : isListening ? 'SUN RAHA HOON' : isLoading ? 'SOCH RAHA HOON' : 'READY'}
                </span>
                <span className="text-xs bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-2 py-0.5 rounded-full font-bold">FREE</span>
              </div>
            </div>

            {/* Main Content */}
            <div className="grid lg:grid-cols-5 gap-0">
              
              {/* Left: Animated Avatar */}
              <div className="lg:col-span-2 relative bg-gradient-to-br from-indigo-900/50 via-purple-900/30 to-blue-900/50 p-4 flex flex-col items-center justify-center min-h-[300px]">
                {/* Background Effects */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className={`absolute top-5 left-5 w-24 h-24 bg-purple-500/20 rounded-full blur-3xl transition-all duration-500 ${isSpeaking ? 'scale-150 opacity-60' : ''}`} />
                  <div className={`absolute bottom-5 right-5 w-32 h-32 bg-cyan-500/20 rounded-full blur-3xl transition-all duration-500 ${isListening ? 'scale-150 opacity-60' : ''}`} />
                  {/* Particles when speaking */}
                  {isSpeaking && [...Array(5)].map((_, i) => (
                    <div 
                      key={i}
                      className="absolute w-2 h-2 bg-yellow-400/60 rounded-full animate-float"
                      style={{
                        left: `${20 + i * 15}%`,
                        bottom: '20%',
                        animationDelay: `${i * 0.2}s`,
                        animationDuration: `${2 + i * 0.5}s`
                      }}
                    />
                  ))}
                </div>
                
                {/* Avatar with realistic movements */}
                <div 
                  className="relative z-10 transition-all duration-100"
                  style={{ 
                    transform: `
                      scale(${breatheScale}) 
                      translateY(${breatheY + shoulderMovement}px)
                      rotateX(${headRotation.x}deg)
                      rotateY(${headRotation.y + emotionConfig.headTilt}deg)
                      rotateZ(${headRotation.z}deg)
                    `,
                  }}
                >
                  {/* Emotion-based Glowing Aura */}
                  <div 
                    className={`absolute -inset-4 rounded-full transition-all duration-500 ${isSpeaking ? 'opacity-90' : 'opacity-50'}`}
                    style={{
                      background: `radial-gradient(circle, ${isSpeaking ? emotionConfig.auraColor : isListening ? 'rgba(239,68,68,0.4)' : 'rgba(234,179,8,0.3)'} 0%, transparent 70%)`,
                      filter: 'blur(12px)',
                      animation: isSpeaking ? 'pulse 1s ease-in-out infinite' : 'none'
                    }}
                  />
                  
                  {/* Emotion indicator ring */}
                  {isSpeaking && (
                    <div 
                      className="absolute -inset-2 rounded-full border-2 transition-all duration-300"
                      style={{
                        borderColor: emotionConfig.glowColor,
                        boxShadow: `0 0 15px ${emotionConfig.glowColor}40`,
                        animation: 'spin 8s linear infinite'
                      }}
                    />
                  )}
                  
                  {/* Main Avatar Circle */}
                  <div 
                    className="relative w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden shadow-xl transition-all duration-300"
                    style={{
                      borderWidth: '3px',
                      borderStyle: 'solid',
                      borderColor: isSpeaking ? emotionConfig.glowColor : 'rgba(234, 179, 8, 0.6)',
                    }}
                  >
                    <img 
                      src={NARAD_IMAGES.cosmic}
                      alt="Digital Narad"
                      className="w-full h-full object-cover object-top transition-all duration-75"
                      style={{
                        transform: `scale(${isSpeaking ? 1.05 : 1}) translateY(${isSpeaking ? lipSyncData.jawDrop * 1.5 : 0}px)`,
                        transformOrigin: 'center 40%',
                        filter: `brightness(${emotionConfig.eyeBrightness})`,
                      }}
                    />
                    
                    {/* Eye tracking overlay */}
                    <div 
                      className="absolute pointer-events-none"
                      style={{
                        top: '28%',
                        left: '50%',
                        transform: `translate(calc(-50% + ${eyePosition.x}px), ${eyePosition.y}px)`,
                        width: '40%',
                        height: '15%',
                        opacity: blinkState ? 0.8 : 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
                        transition: 'opacity 0.1s'
                      }}
                    />
                    
                    {/* Mouth animation overlay - Real-time lip-sync */}
                    {isSpeaking && (
                      <div className="absolute bottom-[20%] left-1/2 -translate-x-1/2 pointer-events-none">
                        {/* Outer lip shape */}
                        <div 
                          className="relative transition-all duration-50"
                          style={{
                            width: `${30 + lipSyncData.mouthWidth * 20}px`,
                            height: `${4 + lipSyncData.mouthOpen * 18 + lipSyncData.jawDrop * 8}px`,
                            transform: `translateX(-50%) translateY(${lipSyncData.jawDrop * 3}px)`,
                          }}
                        >
                          {/* Upper lip */}
                          <div 
                            className="absolute top-0 left-0 right-0 bg-gradient-to-b from-rose-900/40 to-transparent rounded-t-full"
                            style={{
                              height: `${35 + lipSyncData.lipTension * 15}%`,
                              transform: `scaleX(${0.9 + lipSyncData.mouthWidth * 0.2})`,
                            }}
                          />
                          {/* Lower lip */}
                          <div 
                            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-rose-900/50 to-transparent rounded-b-full"
                            style={{
                              height: `${40 + lipSyncData.jawDrop * 20}%`,
                              transform: `translateY(${lipSyncData.jawDrop * 2}px)`,
                            }}
                          />
                          {/* Mouth interior darkness */}
                          <div 
                            className="absolute inset-x-1 top-1/4 bottom-1/4 bg-black/30 rounded-full transition-all duration-50"
                            style={{
                              opacity: lipSyncData.mouthOpen > 0.3 ? 0.6 : 0.2,
                            }}
                          />
                          {/* Teeth hint when mouth open wide */}
                          {lipSyncData.mouthOpen > 0.5 && (
                            <div 
                              className="absolute top-[20%] left-1/2 -translate-x-1/2 bg-white/20 rounded-sm"
                              style={{
                                width: `${50 + lipSyncData.mouthWidth * 30}%`,
                                height: '15%',
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Voice intensity glow - pulses with audio */}
                    {isSpeaking && lipSyncData.volume > 0.1 && (
                      <div 
                        className="absolute inset-0 rounded-full pointer-events-none transition-opacity duration-100"
                        style={{
                          boxShadow: `0 0 ${20 + lipSyncData.volume * 40}px ${lipSyncData.volume * 15}px rgba(34, 197, 94, ${0.2 + lipSyncData.volume * 0.3})`,
                        }}
                      />
                    )}
                    
                    {/* Glow overlay when speaking */}
                    <div 
                      className={`absolute inset-0 transition-opacity duration-200 ${isSpeaking ? 'opacity-100' : 'opacity-0'}`}
                      style={{
                        background: 'radial-gradient(circle at 50% 70%, rgba(34,197,94,0.2) 0%, transparent 50%)'
                      }}
                    />
                  </div>
                  
                  {/* Hand gesture indicator */}
                  {handGesture === 'namaste' && (
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-2xl animate-bounce">
                      🙏
                    </div>
                  )}
                  {handGesture === 'explaining' && isSpeaking && (
                    <div className="absolute -right-4 top-1/2 -translate-y-1/2 text-xl animate-wave">
                      👋
                    </div>
                  )}
                  
                  {/* Emotion indicator */}
                  {isSpeaking && (
                    <div 
                      className="absolute -top-2 -right-2 text-xl animate-bounce"
                      style={{ animationDuration: '1.5s' }}
                    >
                      {emotionConfig.emoji}
                    </div>
                  )}
                  
                  {/* Status Badge with Emotion */}
                  <div 
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold transition-all duration-300"
                    style={{
                      backgroundColor: isSpeaking ? emotionConfig.glowColor : 
                                       isListening ? '#ef4444' :
                                       isLoading ? '#3b82f6' : '#eab308',
                      color: isSpeaking || isListening || isLoading ? 'white' : 'black',
                      transform: isSpeaking ? 'translateX(-50%) scale(1.1)' : 'translateX(-50%)',
                      boxShadow: isSpeaking ? `0 0 10px ${emotionConfig.glowColor}` : 'none'
                    }}
                  >
                    {isSpeaking ? `${emotionConfig.emoji} ${currentEmotion === 'excited' ? 'Excited!' : currentEmotion === 'caring' ? 'Care karta hoon' : currentEmotion === 'sympathetic' ? 'Samajhta hoon' : 'Bol Raha Hoon...'}` : 
                     isListening ? '👂 Sun Raha Hoon...' : 
                     isLoading ? '🤔 Soch Raha Hoon...' : '🙏 Namaste!'}
                  </div>
                </div>
                
                {/* Sound Wave for Listening */}
                {isListening && (
                  <div className="flex items-end gap-1 mt-4 h-8">
                    {[...Array(7)].map((_, i) => (
                      <div 
                        key={i}
                        className="w-1.5 bg-red-500 rounded-full transition-all duration-75"
                        style={{ 
                          height: `${8 + audioLevel * 50 + Math.sin(Date.now() / 100 + i) * 8}px`,
                        }}
                      />
                    ))}
                  </div>
                )}
                
                {/* Real-time Voice Visualizer for Speaking */}
                {isSpeaking && (
                  <div className="flex items-center gap-1 mt-4 h-8">
                    {[...Array(9)].map((_, i) => {
                      const centerIndex = 4;
                      const distFromCenter = Math.abs(i - centerIndex);
                      const heightMultiplier = 1 - (distFromCenter * 0.15);
                      const baseHeight = 6 + lipSyncData.volume * 30 * heightMultiplier;
                      const variation = Math.sin(Date.now() / 80 + i * 0.8) * 5 * lipSyncData.volume;
                      return (
                        <div 
                          key={i}
                          className="w-1 rounded-full transition-all duration-50"
                          style={{ 
                            height: `${Math.max(4, baseHeight + variation)}px`,
                            background: `linear-gradient(180deg, 
                              ${lipSyncData.pitch === 'high' ? '#22c55e' : lipSyncData.pitch === 'low' ? '#f59e0b' : '#3b82f6'} 0%, 
                              ${lipSyncData.pitch === 'high' ? '#16a34a' : lipSyncData.pitch === 'low' ? '#d97706' : '#2563eb'} 100%)`,
                            opacity: 0.6 + lipSyncData.volume * 0.4,
                          }}
                        />
                      );
                    })}
                  </div>
                )}
                
                {/* Control Buttons */}
                <div className="flex gap-2 mt-3 z-10">
                  <button
                    onClick={() => isSpeaking ? stopSpeaking() : speakWithTTS(visitorName ? `Haan ${visitorName} ji! Boliye, main sun raha hoon!` : "Boliye dost! Main sun raha hoon!")}
                    disabled={isLoading}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-sm transition-all shadow-lg disabled:opacity-50 ${
                      isSpeaking 
                        ? 'bg-red-500 hover:bg-red-600 text-white' 
                        : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white'
                    }`}
                  >
                    {isSpeaking ? <><Pause className="w-4 h-4" /> Ruko</> : <><Play className="w-4 h-4" /> Bolo</>}
                  </button>
                  
                  <button
                    onClick={() => setShowSpiritualMode(!showSpiritualMode)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-sm shadow-lg ${
                      showSpiritualMode ? 'bg-orange-500 text-white' : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                    }`}
                  >
                    <Music className="w-4 h-4" />
                    Mantras
                  </button>
                </div>

                {showSpiritualMode && (
                  <div className="mt-2 p-2 bg-orange-500/10 rounded-lg border border-orange-500/30 w-full max-w-xs z-10">
                    <div className="grid grid-cols-2 gap-1">
                      {SPIRITUAL_CONTENT.map((content, idx) => (
                        <button
                          key={idx}
                          onClick={() => playSpiritualContent(content)}
                          className="text-xs px-2 py-1 bg-orange-500/20 hover:bg-orange-500/40 rounded text-orange-200"
                        >
                          {content.hindi}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Interaction Panel */}
              <div className="lg:col-span-3 bg-slate-900/80 p-4 flex flex-col border-l border-yellow-600/20">
                
                {/* Name Input */}
                {showNameInput && !visitorName && (
                  <div className="mb-3 p-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/30 animate-fade-in">
                    <p className="text-yellow-300 mb-2 text-center font-medium text-sm">🙏 Arre dost! Pehle apna naam batao na!</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleNameSubmit()}
                        placeholder="Apna pyaara naam likhein..."
                        className="flex-1 px-3 py-2 bg-white/10 border border-yellow-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 text-sm"
                        autoFocus
                      />
                      <button onClick={handleNameSubmit} className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg text-sm">
                        Chalo! 🚀
                      </button>
                    </div>
                  </div>
                )}

                {visitorName && (
                  <div className="mb-3 p-2 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/30">
                    <p className="text-green-400 text-sm text-center">
                      🙏 <span className="font-bold text-green-300">{visitorName}</span> ji, main hoon na aapke saath! ❤️
                    </p>
                  </div>
                )}

                {/* Display Text */}
                <div className="flex-1 mb-3 p-3 bg-black/30 rounded-lg border border-white/10 min-h-[80px] max-h-[120px] overflow-y-auto">
                  {displayText ? (
                    <p className="text-gray-200 leading-relaxed text-sm">
                      {displayText}
                      {isTyping && <span className="animate-pulse text-yellow-400">|</span>}
                    </p>
                  ) : (
                    <p className="text-gray-500 text-center italic text-sm">
                      {visitorName ? `${visitorName} ji, boliye! 👂` : "Main Digital Narad hoon, aapka dost! 🙏"}
                    </p>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="mb-3">
                  <p className="text-xs text-gray-400 mb-1">Jaldi Madad:</p>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-1">
                    {quickActions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => sendMessage(action.query)}
                        disabled={isSpeaking || isLoading}
                        className="flex flex-col items-center gap-0.5 p-1 bg-white/5 hover:bg-yellow-500/20 border border-white/10 hover:border-yellow-500/30 rounded text-gray-300 hover:text-yellow-300 transition-all disabled:opacity-50"
                      >
                        <span className="text-sm">{action.icon}</span>
                        <span className="text-[8px]">{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Voice & Text Input */}
                <div className="flex gap-2 items-center">
                  <button
                    onClick={isListening ? stopVoiceInput : startVoiceInput}
                    disabled={isSpeaking || isLoading}
                    className={`relative p-2.5 rounded-xl font-bold transition-all shadow-lg disabled:opacity-50 ${
                      isListening ? 'bg-red-500 text-white' : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                    }`}
                  >
                    {isListening ? <PhoneOff className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
                    {isListening && <span className="absolute -inset-1 rounded-xl bg-red-500 opacity-50 animate-ping" />}
                  </button>
                  
                  <div className="flex-1 flex gap-2">
                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage(userInput)}
                      placeholder={visitorName ? `${visitorName} ji, type ya mic dabao...` : "Type ya mic dabao..."}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 text-sm"
                      disabled={isSpeaking || isListening || isLoading}
                    />
                    <button
                      onClick={() => sendMessage(userInput)}
                      disabled={isSpeaking || !userInput.trim() || isListening || isLoading}
                      className="px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 disabled:from-gray-600 disabled:to-gray-700 rounded-lg text-black font-bold disabled:cursor-not-allowed"
                    >
                      {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {isListening && (
                  <div className="mt-2 flex items-center justify-center gap-2 text-red-400 text-xs">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    Boliye... Main dhyan se sun raha hoon! 🎤
                  </div>
                )}

                {/* Voice Selector */}
                <div className="mt-2 flex items-center justify-center gap-2">
                  <span className="text-[10px] text-gray-500">Voice:</span>
                  <select
                    value={voiceType}
                    onChange={(e) => setVoiceType(e.target.value)}
                    className="text-[10px] bg-slate-800 border border-gray-600 rounded px-2 py-0.5 text-gray-300 focus:outline-none focus:border-yellow-500"
                  >
                    {VOICE_OPTIONS.map(v => (
                      v.type === 'divider' ? (
                        <option key={v.id} disabled className="text-gray-500">{v.name}</option>
                      ) : (
                        <option key={v.id} value={v.id}>
                          {v.name} {v.type === 'browser' ? '(FREE)' : ''}
                        </option>
                      )
                    ))}
                  </select>
                  {VOICE_OPTIONS.find(v => v.id === voiceType)?.type === 'browser' && (
                    <span className="text-[8px] text-green-400">✓ Free</span>
                  )}
                </div>

                <p className="text-[8px] text-gray-600 mt-1 text-center">
                  ❤️ Madhur & Swara (Free) • OpenAI HD • 24/7 Aapke Saath
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.6; }
          50% { transform: translateY(-30px) scale(1.2); opacity: 1; }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
        @keyframes wave {
          0%, 100% { transform: translateY(-50%) rotate(0deg); }
          25% { transform: translateY(-50%) rotate(20deg); }
          75% { transform: translateY(-50%) rotate(-20deg); }
        }
        .animate-wave { animation: wave 0.5s ease-in-out infinite; }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.5s ease-out; }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes emotionGlow {
          0%, 100% { box-shadow: 0 0 10px currentColor; }
          50% { box-shadow: 0 0 25px currentColor; }
        }
      `}</style>
    </section>
  );
};

export default DigitalNaradTV;
